/* eslint-disable simple-import-sort/imports */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Controller, Resolver, useForm, type UseFormSetError} from 'react-hook-form';
import {StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {useFocusEffect} from '@react-navigation/native';
import {zodResolver} from '@hookform/resolvers/zod';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';
import rfdc from 'rfdc';

import AcceptOnlineOrderingTCs from '../../../components/rewards/AcceptOnlineOrderingTCs';
import AskToVerify from '../../../components/rewards/AskToVerify';
import OptIn from '../../../components/rewards/OptIn';
import ShopLoyaltyEnabled from '../../../components/rewards/ShopLoyaltyEnabled';
import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';

// Ref type for TextInput - using any due to themed wrapper complexity
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TextInputRef = any;
import AddressEntry from '../../../components/ui-controls/address/AddressEntry';
import BackChevron from '../../../components/ui-controls/BackChevron';
import PrimaryButton from '../../../components/ui-controls/Button';
import DateOfBirthInput from '../../../components/ui-controls/DateOfBirthInput';
import AustralianFlag from '../../../design-system/form/AustralianFlag';
import FieldFrame from '../../../design-system/form/FieldFrame';
import FormField, {fieldChrome, fieldTextStyle} from '../../../design-system/form/FormField';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Modal from '../../../components/ui-controls/Modal';
import {layout, lineHeight, space, type} from '../../../design-system/tokens';
import {useTabBarBottomPadding} from '../../../hooks/useTabBarBottomPadding';
import {AppRoute} from '../../../models/AppRoute';
import {User} from '../../../models/mfl';
import {
  AccountScreenAction,
  InformationContentType,
  RouteNavigatorScreenProps,
} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import rewardsTheme from '../../../theme/rewardsTheme';
import apiService from '../../../transport/apiService';
import {logger, serializeError} from '../../../utils/logger';
import {
  getMissingDetailFields,
  isMissingDateOfBirth,
  type MissingDetailField,
  missingDetailsIntro,
} from '../../../utils/missingDetails';
import {cleanPhoneNumber} from '../../../utils/phoneNumber';
import {rewardsMarketingName} from '../../../utils/rewards';
import {useRewardsTermsAndConditionsPopper} from '../../rewards/TermsAndConditions';
import {PasswordRequirementsIndicator} from '../components/PasswordRequirementsIndicator';
import {WeakPasswordBypass} from '../components/WeakPasswordBypass';
import {usePasswordInput} from '../hooks';
import {
  PASSWORDS_DO_NOT_MATCH,
  useConfirmPasswordMismatch,
} from '../hooks/useConfirmPasswordMismatch';
import SignUpPageLayout, {CTA_HEIGHT, CTA_RADIUS, useWideLayout} from '../signUp/SignUpPageLayout';
import {
  AccountCreateBaseFormData,
  accountCreateBaseSchema,
  AccountCreatePasswordFormData,
  accountCreatePasswordSchema,
  AccountCreateWithPasswordFormData,
} from '../schemas/authSchemas';

import {styles} from './styles';

const clone = rfdc();

export const CREATE_HEADING = 'CREATE AN ACCOUNT';
export const CREATE_INTRO = 'Fill out your details below to create an online shopping account.';
// DIS-1730: DOB_HELPER and MOBILE_HELPER are shared with the Lite sign-up form
// (screens/rewards/SignUp.tsx), so the two flows cannot drift.
export const DOB_HELPER =
  'As a responsible liquor retailer, RSA laws require us to verify your age before you can shop.';
export const MOBILE_HELPER = 'We use your mobile number to verify your account.';
const COLLECT_MISSING_DETAILS_INTRO =
  'We seem to be missing some details from your account. Please verify and fill out the details below to continue.';
export const COLLECT_MISSING_DETAILS_CTA = 'Save and continue';

const EMAIL_LINKED_TO_ANOTHER_ACCOUNT = 'This email is already linked to another account';

/**
 * DIS-1870: GET /ecommerce/email-check-v2 (read-only: MFL email_checks, Zoho
 * contact search, Zen search). ACCOUNT_LOGIN means an MFL account can sign in
 * with this email. A thrown request counts as 'ERROR', like the middleware's
 * own ERROR status. The response interceptor returns the deserialised
 * attributes, not an AxiosResponse.
 */
async function checkNewEmailStatus(email: string): Promise<string> {
  try {
    const result = (await apiService.checkEmail2(email)) as unknown as {status?: string};
    return result?.status ?? 'ERROR';
  } catch (error) {
    logger.error('AccountCreateScreen', 'Email check failed', serializeError(error));
    return 'ERROR';
  }
}
const LEGACY_INTRO =
  'Fill out your details below to create an account to enjoy online shopping and member rewards';

// ============================================================================
// PURE HELPER FUNCTIONS
// ============================================================================

/**
 * Prepare user values for API submission by cleaning up form data
 */
function prepareUserValues(
  values: AccountCreateWithPasswordFormData | AccountCreateBaseFormData,
): Partial<User> {
  const userValues: Partial<User> = clone(values);

  // Remove form-specific fields that shouldn't be sent to API
  delete (userValues as Partial<User> & {optInRewards?: boolean}).optInRewards;

  return userValues;
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Hook for managing form submission state and callbacks
 */
function useFormActions(
  isCreate: boolean,
  passwordRef: React.RefObject<TextInputRef>,
  action: AccountScreenAction | undefined,
) {
  const {userStore} = useStores();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isPasswordUpdatedModalVisible, setIsPasswordUpdatedModalVisible] = useState(false);

  // DIS-1917: collect-missing-details finishes a sign-in that login() or
  // verifyUserEmail() paused, so it continues as a sign-in and may return to a
  // pending offer. An update or password change keeps afterUpdateUser().
  const postSubmitSuccess = useCallback(() => {
    setIsPasswordUpdatedModalVisible(false);
    if (action === AccountScreenAction.COLLECT_MISSING_DETAILS) {
      userStore.afterLogin();
      return;
    }
    userStore.afterUpdateUser();
  }, [action, userStore]);

  const submitSuccessCallback = useCallback(() => {
    setSubmitting(false);
    if (passwordRef.current) {
      passwordRef.current.clear();
    }
    if (isCreate) {
      setIsPasswordUpdatedModalVisible(true);
      return;
    }
    postSubmitSuccess();
  }, [isCreate, passwordRef, postSubmitSuccess]);

  const submitErrorCallback = useCallback(() => {
    setSubmitting(false);
  }, []);

  return {
    isSubmitting,
    setSubmitting,
    isPasswordUpdatedModalVisible,
    submitSuccessCallback,
    submitErrorCallback,
    postSubmitSuccess,
  };
}

/**
 * Hook for phone number validation
 */
function usePhoneVerification(
  isCreate: boolean,
  formSetError: UseFormSetError<AccountCreatePasswordFormData | AccountCreateBaseFormData>,
) {
  const checkPhoneExists = useCallback(
    async (phoneNumber: string): Promise<boolean> => {
      if (!isCreate) return false;

      const cleanedPhone = cleanPhoneNumber(phoneNumber);
      if (cleanedPhone.length < 12) return false;

      try {
        const result = await apiService.rewardsCheckMobileExists({phoneNumber: cleanedPhone});

        // Handle 500 errors with empty response {} - treat as not exists (skip check)
        if (!result || !result.data) {
          return false;
        }

        if (result?.status === 200 && result.data?.exists) {
          formSetError('phoneNumber', {
            type: 'manual',
            message:
              'This mobile number already exists against an account, please enter a different number. If you believe this is an error please contact support.',
          });
          return true; // Phone exists, block submission
        }
      } catch (_e) {
        // Continue if API call fails (treat as phone available)
      }
      return false;
    },
    [isCreate, formSetError],
  );

  return {
    checkPhoneExists,
  };
}

/**
 * Hook for managing the account form setup
 * @param action - Account screen action (CREATE, UPDATE, COLLECT_MISSING_DETAILS)
 * @param email - Email from route params (for CREATE flow)
 * @returns Form methods, watched password value, action booleans, and signup offers
 */
function useAccountForm(action: AccountScreenAction, email?: string) {
  const {userStore, offersStore} = useStores();

  // Trigger loading of sign-up offers when hook is called
  useEffect(() => {
    const _ = offersStore.signUpOfferData; // Accessing triggers lazy load
  }, [offersStore.signUpOfferData]);

  const signUpOffers = offersStore.signUpOfferData || [];

  const isCreate = action === AccountScreenAction.CREATE;
  const isUpdate = action === AccountScreenAction.UPDATE;
  const isCollectMissingDetails = action === AccountScreenAction.COLLECT_MISSING_DETAILS;

  // Select schema based on action
  const schema = isCreate ? accountCreatePasswordSchema : accountCreateBaseSchema;

  const form = useForm<AccountCreatePasswordFormData | AccountCreateBaseFormData>({
    resolver: zodResolver(schema) as Resolver<
      AccountCreatePasswordFormData | AccountCreateBaseFormData
    >,
    defaultValues: {
      firstName: userStore.firstName,
      lastName: userStore.lastName,
      // Prioritize userStore to avoid stale route params. DIS-1870: '' when both are
      // missing, so the schema reports "Email address is required".
      email: userStore.emailAddress || email || '',
      // DIS-1870: a missing date of birth, including MFL's 1900-01-01
      // placeholder, starts empty so the user can enter it and the schema
      // reports it. The placeholder alone passes dateOfBirthSchema.
      dateOfBirth:
        isCollectMissingDetails && isMissingDateOfBirth(userStore.dateOfBirth)
          ? ''
          : userStore.dateOfBirth,
      phoneNumber: userStore.phoneNumber,
      billingAddress: userStore.billingAddress || {
        lines: [],
        locality: '',
        administrative_area: '',
        postal_code: '',
      },
      ...(isCreate && {
        password: '',
        confirmPassword: '',
      }),
      optInRewards: false, // The Opt-in checkbox must be unselected to begin, we cannot preselect this option.
    },
    mode: 'onTouched', // Validate after user interacts with field
  });

  const passwordValue = form.watch('password');

  // Sync email field when userStore.emailAddress changes (for UPDATE action)
  useEffect(() => {
    if (isUpdate && userStore.emailAddress) {
      const currentEmail = form.getValues('email');
      if (currentEmail !== userStore.emailAddress) {
        form.setValue('email', userStore.emailAddress);
      }
    }
  }, [userStore.emailAddress, isUpdate, form]);

  // Reset form when email route param changes (for CREATE action)
  useFocusEffect(
    useCallback(() => {
      if (isCreate && email) {
        const currentEmail = form.getValues('email');
        // If route param email differs from current form email, reset the form
        if (currentEmail !== email) {
          form.reset({
            firstName: '',
            lastName: '',
            email: email,
            dateOfBirth: null,
            phoneNumber: '',
            billingAddress: {
              lines: [],
              locality: '',
              administrative_area: '',
              postal_code: '',
            },
            password: '',
            confirmPassword: '',
            passwordValidationBypass: false,
            optInRewards: false,
          });
        }
      }
    }, [isCreate, email, form]),
  );

  return {
    form,
    passwordValue,
    isCreate,
    isUpdate,
    isCreateOrUpdate: isCreate || isUpdate,
    isCollectMissingDetails,
    signUpOffers,
    isLoadingSignUpOffers: offersStore.isLoadingSignUpOffers,
  };
}

const AccountCreateScreen = observer(
  ({
    route,
    navigation,
  }: RouteNavigatorScreenProps<
    AppRoute.ACCOUNT_CREATE | AppRoute.ACCOUNT_DETAIL | AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS
  >) => {
    const {userStore, rewardsStore} = useStores();
    const isLoggedIn = userStore.isLoggedIn;
    const tabBarPadding = useTabBarBottomPadding();
    const {isDesktopCard: isWide, onLayout} = useWideLayout();

    const action = route.params?.action;
    const email = route.params?.email;

    // Use account form hook for form setup and state
    const accountForm = useAccountForm(action, email);
    const {form, isCreate, isUpdate, isCreateOrUpdate, isCollectMissingDetails} = accountForm;

    // The loyalty terms sentence used to live inside OptIn. It now sits directly
    // under the account T&Cs, so the popper is owned here.
    const loyaltyTerms = useRewardsTermsAndConditionsPopper({
      text: 'Loyalty Terms and Conditions',
      contentType: InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
    });

    // Fetch fresh user info on mount for UPDATE action to ensure email and other fields are current
    useEffect(() => {
      if (isUpdate && isLoggedIn) {
        userStore.retrieveUserInfoV2({retryable: true}).catch(err => {
          logger.error(
            'AccountCreateScreen',
            'Failed to fetch user info on mount',
            serializeError(err),
          );
        });
      }
    }, [isUpdate, isLoggedIn, userStore]);

    // Screen-level error for a failed submit that no field owns.
    const [submitError, setSubmitError] = useState<string | null>(null);

    const onSubmit = async (
      values: AccountCreateWithPasswordFormData | AccountCreateBaseFormData,
    ) => {
      setSubmitError(null);
      formActions.setSubmitting(true);

      // For CREATE flow, check phone number first (no account -> no password)
      const phoneExists = await phoneVerification.checkPhoneExists(values.phoneNumber || '');
      if (phoneExists) {
        formActions.setSubmitting(false); // Reset submitting state
        return; // Stop submission if phone exists
      }

      // DIS-1870: PATCH /ecommerce/user forwards a new email with no uniqueness
      // check, so an email the user typed in is looked up first.
      if (isCollectMissingDetails && missingFields.includes('email')) {
        const emailStatus = await checkNewEmailStatus(values.email || '');
        if (emailStatus === 'ACCOUNT_LOGIN') {
          form.setError('email', {type: 'manual', message: EMAIL_LINKED_TO_ANOTHER_ACCOUNT});
          formActions.setSubmitting(false);
          return;
        }
        if (emailStatus === 'ERROR') {
          setSubmitError('Something went wrong, please try again');
          formActions.setSubmitting(false);
          return;
        }
      }

      // After phone is available, check password strength (if not bypassed)
      if (isCreate && !values.passwordValidationBypass) {
        const _userValues = prepareUserValues(values);

        let passwordIsOkResponse: {password_is_ok?: boolean} | null = null;

        try {
          passwordIsOkResponse = await apiService.checkPasswordStrength(_userValues as User);
        } catch (error: unknown) {
          const axiosError = error as {
            response?: {
              status?: number;
              data?: {
                error?: {
                  errors?: Array<{code?: string; detail?: string}>;
                };
              };
            };
          };

          // A timeout, network failure or 5xx says nothing about the password.
          // Treating it as weak would set a password field error, which keeps
          // NEXT disabled while the field shows no error line.
          const status = axiosError?.response?.status;
          if (status === undefined || status >= 500) {
            setSubmitError('Something went wrong, please try again');
            formActions.setSubmitting(false);
            return;
          }

          const apiErrors = axiosError?.response?.data?.error?.errors;
          const weakErr = apiErrors?.find(err => err.code === 'sign_up_form_password_too_weak');

          formActions.setSubmitting(false);
          passwordInput.handleWeakPasswordError({
            status: 'PASSWORD_TOO_WEAK',
            message: weakErr?.detail || '',
          });
          return;
        }

        if (!passwordIsOkResponse?.password_is_ok) {
          formActions.setSubmitting(false);
          passwordInput.handleWeakPasswordError({
            status: 'PASSWORD_TOO_WEAK',
            message: '',
          });
          return;
        }
      }

      const showSuccessToast = !isCreate;

      if (isLoggedIn) {
        const shouldVerifyMobileNumber = userStore.phoneNumber !== values.phoneNumber;

        const userValues: User = {
          ...values,
          billingAddress: values.billingAddress
            ? {
                ...values.billingAddress,
                lines: values.billingAddress.lines || [],
              }
            : undefined,
        };

        if (isUpdate && shouldVerifyMobileNumber) {
          userStore.updateUserFormValues({
            ...clone(values),
            signUpCode: rewardsStore.signUpCode || undefined,
          });
          await apiService.sendMobileVerificationCode({
            phoneNumber: values.phoneNumber,
          });
          navigation.navigate(AppRoute.ACCOUNT_PHONE_VERIFICATION, {
            action,
          });
        } else {
          await userStore.updateUserInfo(
            action,
            userValues,
            formActions.submitSuccessCallback,
            formActions.submitErrorCallback,
            {
              showSuccessToast,
            },
          );
        }
      } else {
        // CREATE flow: password already validated above
        userStore.updateUserFormValues({
          ...clone(values),
          signUpCode: rewardsStore.signUpCode || undefined,
        });
        await apiService.sendMobileVerificationCode({
          phoneNumber: values.phoneNumber,
        });

        formActions.setSubmitting(false);
        navigation.navigate(AppRoute.ACCOUNT_PHONE_VERIFICATION);
      }
      formActions.setSubmitting(false);
    };

    const passwordRef = useRef<TextInputRef>(null);
    const confirmPasswordRef = useRef<TextInputRef>(null);

    // Use form actions hook for submission state and callbacks
    const formActions = useFormActions(isCreate, passwordRef, action);

    // Password input hook (visibility, focus, weak password handling)
    const passwordInput = usePasswordInput({
      form,
      confirmFieldName: 'confirmPassword',
      passwordRef,
      confirmPasswordRef,
    });

    // Extract reset for cleanup
    const {reset: resetPasswordInput} = passwordInput;

    // DIS-1862: the mismatch line shows on blur, not while typing.
    const confirmMismatch = useConfirmPasswordMismatch(
      passwordInput.passwordValue,
      form.watch('confirmPassword'),
      confirmPasswordRef,
    );

    // Phone verification hook
    const phoneVerification = usePhoneVerification(isCreate, form.setError);

    // DIS-1870: the fields missing when the screen opened. Captured once, so a
    // field the user is filling in does not lock as soon as it has a value.
    const [missingFields] = useState<MissingDetailField[]>(
      isCollectMissingDetails ? getMissingDetailFields(userStore) : [],
    );
    const lockedUnlessMissing = (field: MissingDetailField) =>
      isCollectMissingDetails && !missingFields.includes(field);
    const disableField = {
      email: !missingFields.includes('email'),
      firstName: lockedUnlessMissing('firstName'),
      lastName: lockedUnlessMissing('lastName'),
      dateOfBirth: lockedUnlessMissing('dateOfBirth'),
      phoneNumber: lockedUnlessMissing('phoneNumber'),
      billingAddress: lockedUnlessMissing('billingAddress'),
      mobileNumber: lockedUnlessMissing('phoneNumber'),
    };

    // DIS-1870: show each missing field's red error line on arrival, not only
    // after a touch or a submit, so the user can see what needs attention.
    useEffect(() => {
      missingFields.forEach(field => {
        form.setValue(field, form.getValues(field), {shouldTouch: true, shouldValidate: true});
      });
    }, [form, missingFields]);

    // Reset password input state when navigating away
    useFocusEffect(
      useCallback(() => {
        return () => {
          resetPasswordInput();
        };
      }, [resetPasswordInput]),
    );

    // A submit with mismatched passwords is blocked by the schema; onInvalid
    // shows the mismatch line and focuses Confirm password.
    const handleSubmitPress = form.handleSubmit(onSubmit, confirmMismatch.onInvalidSubmit);
    const isSubmitted = form.formState.isSubmitted;
    const showFields = isCreateOrUpdate || isCollectMissingDetails;

    // Gated on isCreate as well as width for two independent reasons.
    // ACCOUNT_DETAIL keeps `back: true` in its own route header, so an ungated
    // chevron would give that screen two back controls. ACCOUNT_COLLECT_MISSING_DETAILS
    // is the harder case: its header runs in blinker mode with no back arrow,
    // no burger and no search, because the flow is deliberately forced to
    // completion. A chevron there would hand it an escape hatch it is designed
    // not to have. Do not reduce this to `!isWide`.
    const backChevron =
      !isWide && isCreate ? (
        <View style={localStyles.backRow}>
          <BackChevron onPress={() => navigation.goBack()} testID="accountCreateBack" />
        </View>
      ) : null;

    // One field tree serves both layouts: stacked, the main/aside split renders in
    // exactly the order the mobile comp asks for, so no width branching is needed
    // inside the form itself.
    const fields = (
      <View
        style={[
          localStyles.fieldStack,
          // CREATE only (83:794, 160:8197): the legacy branch keeps its gap.
          isCreate && (isWide ? localStyles.createFieldStackWide : localStyles.createFieldStack),
        ]}>
        <Controller
          control={form.control}
          name="email"
          render={({field: {onChange, onBlur, value}, fieldState: {isTouched, error}}) => (
            <FormField
              label="Email"
              required
              autoComplete="email"
              keyboardType="email-address"
              maxLength={100}
              editable={!disableField.email}
              onBlur={onBlur}
              onChangeText={onChange}
              value={value || ''}
              error={!!error && (isTouched || isSubmitted)}
              errorMessage={error?.message}
              testID="email"
            />
          )}
        />
        <Controller
          control={form.control}
          name="firstName"
          render={({field: {onChange, onBlur, value}, fieldState: {isTouched, error}}) => (
            <FormField
              label="First name"
              required
              autoCapitalize="words"
              autoComplete="name-given"
              keyboardType="default"
              maxLength={50}
              editable={!disableField.firstName}
              onBlur={onBlur}
              onChangeText={onChange}
              value={value || ''}
              error={!!error && (isTouched || isSubmitted)}
              errorMessage={error?.message}
              testID="firstName"
            />
          )}
        />
        <Controller
          control={form.control}
          name="lastName"
          render={({field: {onChange, onBlur, value}, fieldState: {isTouched, error}}) => (
            <FormField
              label="Last name"
              required
              autoCapitalize="words"
              autoComplete="name-family"
              keyboardType="default"
              maxLength={50}
              editable={!disableField.lastName}
              onBlur={onBlur}
              onChangeText={onChange}
              value={value || ''}
              error={!!error && (isTouched || isSubmitted)}
              errorMessage={error?.message}
              testID="lastName"
            />
          )}
        />
        <View style={styles.dateOfBirthContainer}>
          <Controller
            control={form.control}
            name="dateOfBirth"
            render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
              <FieldFrame
                label="Date of birth"
                required
                description={DOB_HELPER}
                error={!!error && (isTouched || isSubmitted)}
                errorMessage={error?.message}
                testID="dateOfBirth">
                <DateOfBirthInput
                  initialDate={value}
                  disabled={isUpdate || disableField.dateOfBirth}
                  onChange={dateString => {
                    onChange(dateString);
                    // Trigger validation after user completes date entry
                    setTimeout(() => onBlur(), 0);
                  }}
                  onInvalidDate={errorMessage => {
                    form.setError('dateOfBirth', {type: 'manual', message: errorMessage});
                  }}
                />
              </FieldFrame>
            )}
          />
        </View>
        <Controller
          control={form.control}
          name="billingAddress"
          render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
            <FieldFrame
              label="Billing Address"
              required
              error={!!error && (isTouched || isSubmitted)}
              errorMessage="Please enter a valid billing address"
              testID="billingAddressField">
              <AddressEntry
                testID="billingAddress"
                onChange={address => {
                  onChange(address);
                  // Trigger validation after user completes address entry
                  setTimeout(() => onBlur(), 0);
                }}
                address={
                  value || {
                    lines: [],
                    locality: '',
                    administrative_area: '',
                    postal_code: '',
                  }
                }
                placeholder=""
                disabled={disableField.billingAddress}
              />
            </FieldFrame>
          )}
        />
        {!!isCreate && (
          <>
            <Controller
              control={form.control}
              name="password"
              render={({field: {onChange, onBlur, value}}) => (
                <>
                  {/* No error line: PasswordRequirementsIndicator below is the
                      feedback surface for this field, as it was before. */}
                  <FormField
                    label="Password"
                    required
                    secureTextEntry
                    visibilityToggle
                    onBlur={() => {
                      passwordInput.passwordFieldHandlers.onBlur();
                      onBlur();
                    }}
                    onFocus={passwordInput.passwordFieldHandlers.onFocus}
                    onChangeText={(text: string) => {
                      onChange(text);
                      passwordInput.passwordFieldHandlers.onChangeText(text);
                    }}
                    onSubmitEditing={passwordInput.passwordFieldHandlers.onSubmitEditing}
                    ref={passwordRef}
                    value={value || ''}
                    testID="password"
                  />
                  <PasswordRequirementsIndicator {...passwordInput.indicatorProps} />
                </>
              )}
            />
            <WeakPasswordBypass {...passwordInput.bypassCheckboxProps} />
            <Controller
              control={form.control}
              name="confirmPassword"
              render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => {
                // DIS-1862: the schema's own mismatch error re-validates on
                // every keystroke, so it is never shown; the hook decides when
                // the mismatch line appears. Other errors (required) still show.
                const {showMismatch, mismatch} = confirmMismatch;
                const schemaError = mismatch ? undefined : error;

                return (
                  <FormField
                    label="Confirm password"
                    required
                    secureTextEntry
                    visibilityToggle
                    onBlur={() => {
                      confirmMismatch.onConfirmBlur();
                      onBlur();
                    }}
                    onChangeText={(text: string) => {
                      onChange(text);
                      passwordInput.confirmFieldHandlers.onChangeText(text);
                    }}
                    ref={confirmPasswordRef}
                    value={value || ''}
                    error={showMismatch || (!!schemaError && (isTouched || isSubmitted))}
                    errorMessage={showMismatch ? PASSWORDS_DO_NOT_MATCH : schemaError?.message}
                    testID="confirmPassword"
                  />
                );
              }}
            />
          </>
        )}
        <Controller
          control={form.control}
          name="phoneNumber"
          render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
            <FormField
              label="Mobile"
              required
              // Only CREATE gets the helper sentence: the update path still
              // carries AskToVerify below the field.
              description={isCreate ? MOBILE_HELPER : undefined}
              autoComplete="tel"
              keyboardType="phone-pad"
              maxLength={20}
              editable={!disableField.mobileNumber}
              onBlur={onBlur}
              onChangeText={onChange}
              value={value || ''}
              // Only CREATE gets the +61 prefix: the other actions prefill a
              // stored number that already carries its own prefix.
              prefix={
                isCreate ? (
                  <>
                    <AustralianFlag />
                    <Text style={[fieldTextStyle, localStyles.dialCode]}>+61</Text>
                  </>
                ) : undefined
              }
              prefixAccessibilityHint={isCreate ? 'Australian number, +61' : undefined}
              placeholder={isCreate ? '412 345 678' : 'e.g. 0412 345 678 or +61 412 345 678'}
              error={!!error && (isTouched || isSubmitted)}
              errorMessage={error?.message}
              testID="phoneNumber"
            />
          )}
        />
      </View>
    );

    const submitErrorNode = submitError ? (
      <Text
        variant="body2"
        style={localStyles.submitError}
        accessibilityRole="alert"
        accessibilityLiveRegion="polite"
        testID="accountCreateSubmitError">
        {submitError}
      </Text>
    ) : null;

    const aside = (
      <View style={localStyles.aside}>
        <View style={localStyles.asideCopy}>
          <ShopLoyaltyEnabled>
            <Controller
              control={form.control}
              name="optInRewards"
              render={({field: {onChange, value}}) => (
                <OptIn checked={!!value} onChange={() => onChange(!value)} />
              )}
            />
          </ShopLoyaltyEnabled>
          <AcceptOnlineOrderingTCs />
          <Text variant="body2" style={localStyles.loyaltyTerms} testID="loyalty-terms-text">
            When signing up to the {rewardsMarketingName} program, you agree with the{' '}
            {loyaltyTerms.textNode}
          </Text>
          {loyaltyTerms.dialogNode}
        </View>
        <View style={localStyles.cta}>
          {submitErrorNode}
          <PrimaryButton
            title="NEXT"
            onPress={handleSubmitPress}
            // Subscribing to isValid is what makes react-hook-form re-validate the
            // whole form on change, which the grey-until-valid gate needs.
            disabled={!form.formState.isValid || formActions.isSubmitting}
            loading={formActions.isSubmitting}
            buttonStyle={localStyles.next}
            disabledStyle={localStyles.nextDisabled}
            titleStyle={localStyles.nextTitle}
            disabledTitleStyle={localStyles.nextTitleDisabled}
            testID="accountCreateNext"
          />
        </View>
      </View>
    );

    return (
      <ThemeProvider theme={rewardsTheme}>
        <KeyboardAwareScrollView
          style={localStyles.screen}
          keyboardShouldPersistTaps="always"
          enableOnAndroid
          extraScrollHeight={40}
          contentContainerStyle={{paddingBottom: tabBarPadding}}>
          {isCreate ? (
            <SignUpPageLayout
              isWide={isWide}
              onLayout={onLayout}
              testID="accountCreateScreen"
              above={
                <View style={[localStyles.above, isWide && localStyles.aboveWide]}>
                  {backChevron}
                  <Text align="center" style={localStyles.heading} testID="accountCreateHeading">
                    {CREATE_HEADING}
                  </Text>
                  <Text
                    align="center"
                    style={
                      isWide ? [type.paragraph.p1, lineHeight.paragraph.p1] : type.paragraph.p2
                    }>
                    {CREATE_INTRO}
                  </Text>
                </View>
              }
              main={fields}
              aside={aside}
              asideAlign="top"
              stackedGap={layout.mobile.stackGroup}
            />
          ) : (
            <ContentView>
              {backChevron}
              <View style={localStyles.legacyStack}>
                {!!showFields && (
                  <>
                    <Text style={localStyles.legacyIntro}>
                      {isCollectMissingDetails
                        ? (missingDetailsIntro(missingFields) ?? COLLECT_MISSING_DETAILS_INTRO)
                        : LEGACY_INTRO}
                    </Text>
                    {fields}
                    {!disableField.mobileNumber && <AskToVerify action={action} />}
                  </>
                )}
                <View style={localStyles.legacyButtonRow}>
                  {/* DIS-1870: a failed email check before the save shows here. */}
                  {submitErrorNode}
                  <Button
                    title={
                      isCollectMissingDetails
                        ? COLLECT_MISSING_DETAILS_CTA
                        : isLoggedIn
                          ? 'Save'
                          : 'Next'
                    }
                    onPress={handleSubmitPress}
                    disabled={formActions.isSubmitting}
                    loading={formActions.isSubmitting}
                    buttonStyle={localStyles.legacyButton}
                  />
                </View>
              </View>
            </ContentView>
          )}
          <Modal
            isVisible={formActions.isPasswordUpdatedModalVisible}
            title="Password updated successfully">
            <Button
              title="Continue"
              onPress={formActions.postSubmitSuccess}
              buttonStyle={{width: 150, marginTop: 16}}
            />
          </Modal>
        </KeyboardAwareScrollView>
      </ThemeProvider>
    );
  },
);

export default AccountCreateScreen;

// SHORTCUT: 30 is the legacy section rhythm of the non-CREATE branch only;
// the CREATE layout takes its gaps from `layout` and `space`. 30 is off the
// spacing scale. Ceiling: the non-CREATE `fieldStack`. Upgrade path: move
// that branch onto `layout.*` when it is redesigned - DIS-1735 is the epic.
const SECTION_GAP = 30;
// SHORTCUT: spacing literals - the four constants below, extraScrollHeight 40
// and the Continue button's {width: 150, marginTop: 16} in the render above.
// LEGACY_STACK_GAP and LEGACY_BUTTON_WIDTH serve only the non-CREATE branch.
// Ceiling: this screen. Upgrade path: the same spacing module as SECTION_GAP.
const ABOVE_GAP = 16;
const LEGACY_STACK_GAP = 8;
const LEGACY_BUTTON_WIDTH = 194;
const SUBMIT_ERROR_GAP = 8;

const localStyles = StyleSheet.create({
  screen: {
    backgroundColor: theme.colour.background.general.screen,
  },
  // Header -> back 16, back -> heading -> intro 16, intro -> first field 24
  // (83:794). Desktop (160:8197): header -> heading and intro -> first field
  // are the desktop section gaps.
  above: {
    gap: layout.mobile.stackRelated,
    paddingTop: space.s16,
    paddingBottom: layout.mobile.stackGroup,
  },
  aboveWide: {
    paddingTop: layout.desktop.headerToContent,
    paddingBottom: layout.desktop.stackSection,
  },
  backRow: {
    alignItems: 'flex-start',
  },
  heading: {
    ...type.heading.h1,
    color: theme.colour.text.heading.primary,
  },
  fieldStack: {
    gap: SECTION_GAP,
  },
  // Field -> field: 32 on mobile (83:794), 24 on desktop (160:8197). No layout
  // key carries a field gap yet, so these read the scale, as SignUp.tsx does.
  createFieldStack: {
    gap: space.s32,
  },
  createFieldStackWide: {
    gap: space.s24,
  },
  // Opt-in, T&C and loyalty terms 24 apart; NEXT 40 below them (83:794).
  aside: {
    gap: space.s40,
  },
  asideCopy: {
    gap: space.s24,
  },
  // Keeps a submit error tight above NEXT instead of a section gap away.
  cta: {
    gap: SUBMIT_ERROR_GAP,
  },
  // The colour the themed Text's color="error" resolves to, read directly
  // because that prop is not in the Text type.
  submitError: {
    color: rewardsTheme.lightColors?.error,
  },
  // SHORTCUT: 'italic' is a type literal. No typography token carries a font
  // style. Ceiling: the loyalty terms line. Upgrade path: an italic variant
  // under DIS-1735, if design keeps the italic.
  loyaltyTerms: {
    fontStyle: 'italic',
  },
  // The dial code reads as part of the typed number, so its family, size and
  // weight come from FormField's own `fieldTextStyle` (applied at the call
  // site, not spread here) rather than a token, so it cannot drift from the
  // input text beside it. It takes its colour from the field chrome: the
  // prefix only renders on CREATE, where the field is always editable, and
  // every editable state shares that colour.
  dialCode: {
    color: fieldChrome('default').color,
  },
  // Height and radius are pinned, not left to the button's own sizing: the
  // shared Button hardcodes @rneui's size="sm", which renders 27px tall. They
  // come from SignUpPageLayout so this CTA and the email step's cannot drift.
  next: {
    width: '100%',
    height: CTA_HEIGHT,
    borderRadius: CTA_RADIUS,
    backgroundColor: theme.colour.background.button.primary,
  },
  nextDisabled: {
    backgroundColor: theme.colour.background.button.disabled,
  },
  // @rneui's Button sets no font family, so the token carries family and weight
  // together through resolveWeight, the only boundary that gets bold right on
  // Android.
  nextTitle: {
    ...type.button.primary,
    color: theme.colour.background.button.contrastOnPrimary,
  },
  nextTitleDisabled: {
    color: theme.colour.text.button.disabled,
  },
  legacyStack: {
    gap: LEGACY_STACK_GAP,
  },
  legacyIntro: {
    marginVertical: ABOVE_GAP,
  },
  legacyButtonRow: {
    alignItems: 'flex-end',
    marginTop: ABOVE_GAP,
  },
  legacyButton: {
    width: LEGACY_BUTTON_WIDTH,
  },
});
